import { FormFieldType } from "~/components/form/fields";
import type { FormStructure } from "~/types/ui";

export const SEPARATOR = ";";

export function flattenObject(obj: any, prefix = ""): Record<string, any> {
  return Object.keys(obj).reduce(
    (acc, key) => {
      const value = obj[key];
      const newKey = prefix ? `${prefix}.${key}` : key;

      if (value && typeof value === "object" && !Array.isArray(value)) {
        Object.assign(acc, flattenObject(value, newKey));
      } else {
        acc[newKey] = value;
      }

      return acc;
    },
    {} as Record<string, any>,
  );
}

export function exportToCsv(filename: string, rows: any[]) {
  if (!rows.length) return;

  // Flatten all rows first
  const flattened = rows.map((r) => flattenObject(r));

  // Collect all unique keys (columns)
  const headers = Array.from(
    new Set(flattened.flatMap((row) => Object.keys(row))),
  );

  const csv = [
    headers.join(SEPARATOR), // header row
    ...flattened.map((row) =>
      headers
        .map((h) => trimStringify(JSON.stringify(row[h] ?? "")))
        .join(SEPARATOR),
    ),
  ].join("\n");

  // Trigger download
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// Escape values for CSV (handle semicolons, quotes, newlines) per RFC 4180:
// quote the field and double any internal quotes whenever it contains a quote,
// the separator, or a newline.
function escapeCsvValue(value: any): string {
  if (value == null) return "";
  // typeof value === "object" both handles objects and arrays
  const str = typeof value === "object" ? JSON.stringify(value) : String(value);

  if (str.includes('"') || str.includes(SEPARATOR) || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

// Quote-aware CSV line split: treats `""` inside a quoted field as a literal
// `"`, and doesn't split on the separator while inside a quoted field. Does
// not support a quoted field spanning multiple physical lines.
function parseCsvLine(line: string): string[] {
  const values: string[] = [];
  let current = "";
  let inQuotes = false;
  // A quote only opens a quoted field when it is the first character of that
  // field. Anywhere else it is a literal character: otherwise a stray quote in
  // an unquoted field (e.g. `12" pipe`) swallows every following separator and
  // the whole row is discarded by the column count check.
  let atFieldStart = true;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (inQuotes) {
      if (char === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
    } else if (char === '"' && atFieldStart) {
      inQuotes = true;
      atFieldStart = false;
    } else if (char === SEPARATOR) {
      values.push(current);
      current = "";
      atFieldStart = true;
    } else {
      current += char;
      atFieldStart = false;
    }
  }
  values.push(current);

  return values;
}

function trimStringify(value: string): string {
  let str = value;
  if (str.startsWith('"') && str.endsWith('"')) {
    str = str.slice(1, -1);
  }

  return str;
}

export function exportFormCsv<T>(
  rows: T[],
  structure: FormStructure<T>,
  filename = "data.csv",
) {
  const headers = Object.keys(structure);

  const csvRows = [
    headers
      .map((key) => key)
      .filter((d) => d !== "virtualFields")
      .join(SEPARATOR), // header row
    ...rows.map((row) =>
      headers
        .filter((d) => d !== "virtualFields")
        .map((key) => escapeCsvValue((row as any)[key]))
        .join(SEPARATOR),
    ),
  ];

  const csv = csvRows.join("\n");

  // trigger download
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function parseValue(
  value: string,
  type: FormFieldType = FormFieldType.TEXT,
): any {
  // Add custom parsing logic based on key or expected type if needed
  // For now, just return the string or convert to number/boolean if applicable
  if (value === "") return "";

  // Custom rules, like for deveui / hex fields, we keep as string even tho they
  // could be parsed as numbers. If we ever need to have something custom for
  // TEXT please update the default value of this function.
  if (type === FormFieldType.HEX) return value;

  if (!isNaN(Number(value))) return Number(value);
  if (value.toLowerCase() === "true") return true;
  if (value.toLowerCase() === "false") return false;
  if (value.startsWith("[") && value.endsWith("]")) {
    // If array
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }

  return value;
}

function getDefaultImportValueByStructure<T>(
  key: string,
  structure: FormStructure<T>,
) {
  if (key === "virtualFields") return undefined;
  const def = structure[key as keyof T];
  switch (def.type) {
    case FormFieldType.CHECKBOX:
      return false;
    default:
      return undefined;
  }
}

export function importFormCsv<T>(
  file: File,
  structure: FormStructure<T>,
  onImport: (rows: T[]) => void,
  ignoreIdxZero = false,
) {
  const reader = new FileReader();
  reader.onload = (event) => {
    const text = event.target?.result as string;
    if (!text) return;

    const lines = text.split(/\r?\n/).filter(Boolean);
    if (lines.length < 2) {
      console.warn("Not enough rows in CSV File");
      return;
    }

    const headers = parseCsvLine(lines[0]).map((h) => h.trim());
    const keys = Object.keys(structure);

    const rows: (T | undefined)[] = lines.slice(1).map((line) => {
      const values = parseCsvLine(line).map((v) => v.trim());
      if (values.length !== headers.length) {
        console.warn("Row length does not match header length:", line);
        return;
      }

      const mapped: any = {};
      keys.forEach((key) => {
        const idx = headers.indexOf(key);
        if (key === "virtualFields") return; // skip virtual fields during import

        if (ignoreIdxZero && idx <= 0) {
          mapped[key] = getDefaultImportValueByStructure(key, structure);
          return;
        }

        let parsedValue = parseValue(
          values[idx] ?? "",
          structure[key as keyof T]?.type,
        );
        if (parsedValue == "")
          parsedValue = getDefaultImportValueByStructure(key, structure);

        mapped[key] =
          idx >= 0
            ? parsedValue
            : getDefaultImportValueByStructure(key, structure);
      });

      return mapped as T;
    });

    onImport(rows.filter((r): r is T => r !== undefined));
  };
  reader.readAsText(file);
}
